import React, { useMemo } from 'react';
import { Box, Slider, Stack, Typography } from '@mui/material';

interface BudgetRangeSliderProps {
  value: [number, number];
  onChange: (value: [number, number]) => void;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean;
}

export const BudgetRangeSlider: React.FC<BudgetRangeSliderProps> = ({
  value,
  onChange,
  min = 1,
  max = 10000,
  step = 1,
  error = false,
}) => {
  const normalizedValue = useMemo<[number, number]>(() => {
    const from = Number.isFinite(value[0]) ? value[0] : min;
    const to = Number.isFinite(value[1]) ? value[1] : from;

    return [Math.max(min, from), Math.max(min, to)];
  }, [min, value]);

  const sliderMax = useMemo(() => {
    // Если в шаблоне уже лежит значение больше дефолтного максимума,
    // не обрезаем его и даем спокойно редактировать существующий диапазон.
    return Math.max(max, normalizedValue[1]);
  }, [max, normalizedValue]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
        <Typography variant="body2">От: {normalizedValue[0]} USD</Typography>
        <Typography variant="body2">До: {normalizedValue[1]} USD</Typography>
      </Stack>

      <Box sx={{ px: 2 }}>
        <Slider
          value={normalizedValue}
          min={min}
          max={sliderMax}
          step={step}
          onChange={(_, newValue) => {
            if (Array.isArray(newValue) && newValue.length === 2) {
              onChange([newValue[0], newValue[1]]);
            }
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={(sliderValue) => `${sliderValue} USD`}
          disableSwap
          marks={[
            { value: min, label: `${min}` },
            { value: sliderMax, label: `${sliderMax}` },
          ]}
          sx={{
            mt: 1,
            width: 'calc(100% - 16px)',
            mx: 'auto',
            ...(error ? { color: 'error.main' } : {}),
          }}
        />
      </Box>

      <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
        {error
          ? 'Не удалось определить корректный диапазон бюджета.'
          : 'Перетащите ползунки, чтобы задать диапазон бюджета в USD.'}
      </Typography>
    </Box>
  );
};
